import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, BookOpen, X } from "lucide-react";

const SEEN_KEY = "ww_guide_popup_seen";

export function ExitIntentGuide() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [instagram, setInstagram] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(SEEN_KEY)) return;
    let shown = false;
    const show = () => {
      if (shown || localStorage.getItem(SEEN_KEY)) return;
      shown = true;
      setOpen(true);
    };
    const onMouseOut = (e) => {
      if (e.clientY <= 8 && !e.relatedTarget) show();
    };
    const timer = setTimeout(show, 45000);
    document.addEventListener("mouseout", onMouseOut);
    return () => { document.removeEventListener("mouseout", onMouseOut); clearTimeout(timer); };
  }, []);

  const dismiss = () => {
    localStorage.setItem(SEEN_KEY, "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/lead-magnet", { email, name, instagram, origin_url: window.location.origin });
      localStorage.setItem(SEEN_KEY, "1");
      setDone(true);
      toast.success("Your guide is on its way to your inbox");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-5"
      style={{ background: "rgba(20,19,17,0.55)", backdropFilter: "blur(4px)" }}
      onClick={dismiss} data-testid="guide-popup-overlay">
      <div className="relative w-full max-w-md p-8 md:p-10 text-center"
        style={{ background: "#141311", color: "#F8F5EF", border: "1px solid rgba(168,152,128,0.35)" }}
        onClick={(e) => e.stopPropagation()} data-testid="guide-popup">
        <button className="absolute top-4 right-4 p-1 opacity-70 hover:opacity-100 transition-opacity"
          onClick={dismiss} aria-label="Close" data-testid="guide-popup-close" style={{ color: "#F8F5EF" }}>
          <X size={18} />
        </button>
        <p className="lp-label-sm" style={{ color: "#A89880" }}>Before you go — a gift</p>
        <h3 className="lp-h mt-4 text-3xl" style={{ color: "#FFFFFF" }}>
          The Metabolism<br />
          <span className="lp-it normal-case" style={{ color: "#A89880" }}>Reset Guide.</span>
        </h3>
        <p className="lp-it mt-5 text-lg leading-relaxed" style={{ color: "#D4CFC7" }}>
          5 signs your metabolism is asking for help — and the first 3 steps to heal it. Free.
        </p>
        {done ? (
          <button className="lp-btn lp-btn-fill mt-7 w-full" onClick={() => { dismiss(); navigate("/guide"); }} data-testid="guide-popup-read">
            <BookOpen size={13} /> Read the guide now <ArrowRight size={12} />
          </button>
        ) : (
          <form onSubmit={submit} className="mt-7 space-y-3" data-testid="guide-popup-form">
            <input className="input-luxe w-full" placeholder="First name" value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(168,152,128,0.4)", color: "#FFFFFF" }}
              data-testid="guide-popup-name" />
            <input className="input-luxe w-full" required placeholder="Instagram handle (@you)" value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(168,152,128,0.4)", color: "#FFFFFF" }}
              data-testid="guide-popup-instagram" />
            <input className="input-luxe w-full" type="email" required placeholder="Your email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(168,152,128,0.4)", color: "#FFFFFF" }}
              data-testid="guide-popup-email" />
            <button className="lp-btn lp-btn-fill w-full" disabled={busy} data-testid="guide-popup-submit">
              {busy ? "Sending…" : "Send my free guide"} <ArrowRight size={12} />
            </button>
            <button type="button" className="lp-label-sm opacity-60 hover:opacity-100 transition-opacity mt-1"
              onClick={dismiss} style={{ color: "#D4CFC7", background: "none", border: "none", cursor: "pointer" }}
              data-testid="guide-popup-no-thanks">
              No thanks, I'll keep guessing
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
